import { useEffect, useRef, useState } from "react";
import useApi from "./useApi";

/**
 * Valida a matrícula de um colaborador contra o Protheus (SRA010) e retorna
 * NOME, FUNÇÃO e CENTRO DE CUSTO. A validação só dispara quando a matrícula
 * tiver exatamente 6 dígitos.
 *
 * @param {string} matricula
 * @param {{ bloquearDemitido?: boolean }} [options]
 *   - bloquearDemitido (default: true): quando true, seta um erro de validação
 *     se o colaborador estiver desligado (RA_DEMISSA <> ''). No offboard isso
 *     deve ser false, pois queremos permitir o offboard de demitidos.
 *
 * @returns {{ info: object|null, validating: boolean, error: string }}
 */
export default function useValidateColaborador(matricula, options = {}) {
  const { bloquearDemitido = true } = options;

  const api = useApi();
  const apiRef = useRef(api);

  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [validating, setValidating] = useState(false);
  const requestId = useRef(0);

  useEffect(() => {
    const mat = String(matricula || "").trim();
    const currentId = requestId.current + 1;
    requestId.current = currentId;

    if (mat.length !== 6) {
      setInfo(null);
      setError(mat.length === 0 ? "" : "A matrícula deve conter 6 dígitos.");
      setValidating(false);
      return;
    }

    const run = async () => {
      try {
        setValidating(true);
        setError("");
        const { data } = await apiRef.current.get(
          `/Painel/API/InventarioTI/ValidarResponsavel/?matricula=${mat}`
        );

        if (requestId.current !== currentId) return;

        if (bloquearDemitido && data?.demitido) {
          setInfo(data);
          setError("Colaborador desligado. Não é possível atribuir responsável.");
        } else {
          setInfo(data);
          setError("");
        }
      } catch (err) {
        if (requestId.current !== currentId) return;
        setInfo(null);
        setError(
          err?.response?.data?.message || "Erro ao validar matrícula."
        );
      } finally {
        if (requestId.current === currentId) {
          setValidating(false);
        }
      }
    };

    run();
  }, [matricula, bloquearDemitido]);

  return { info, validating, error };
}
